import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';

/**
 * Single-select dropdown with a built-in search box, for long option lists
 * (e.g. the ~200 SAP job IDs) where a native <select> is unsearchable. Same
 * portal + viewport-anchored positioning as MultiSelect so it escapes card
 * overflow and flips above when there is no room below.
 */

export interface SearchableOption {
  value: string;
  label: string;
  /** Secondary line, e.g. a code beneath the human name. */
  description?: string;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  className?: string;
  menuMinWidth?: number;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Choose…',
  searchPlaceholder = 'Search…',
  ariaLabel,
  className = '',
  menuMinWidth = 280,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    placement: 'below' | 'above';
  } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    function reposition() {
      const t = triggerRef.current;
      if (!t) return;
      const rect = t.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const desired = 320 + 8;
      const placement: 'below' | 'above' =
        spaceBelow < desired && spaceAbove > spaceBelow ? 'above' : 'below';
      const viewportMargin = 8;
      const maxWidth = window.innerWidth - viewportMargin * 2;
      const width = Math.min(Math.max(rect.width, menuMinWidth), maxWidth);
      let left = rect.left;
      if (width > rect.width) {
        const rightAligned = rect.right - width;
        if (rightAligned >= viewportMargin) left = rightAligned;
        left = Math.min(Math.max(left, viewportMargin), window.innerWidth - width - viewportMargin);
      }
      setPosition({
        top: placement === 'below' ? rect.bottom + 4 : rect.top - 4,
        left,
        width,
        placement,
      });
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, menuMinWidth]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const selected =
    options.find((o) => o.value === value) ?? (value != null ? { value, label: value } : null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  function choose(next: string | null) {
    onChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-9 w-full items-center gap-1.5 rounded-token-sm border border-line/20 bg-surface px-2 py-1.5 text-left text-sm transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong"
      >
        <span className={`truncate ${selected ? '' : 'text-fg-dim'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="listbox"
              style={{
                position: 'fixed',
                top: position.placement === 'below' ? position.top : undefined,
                bottom: position.placement === 'above' ? window.innerHeight - position.top : undefined,
                left: position.left,
                width: position.width,
              }}
              className="aurora-fade-in z-[1000] flex max-h-80 flex-col overflow-hidden rounded-token border border-line/10 bg-surface shadow-token-lg"
            >
              <div className="border-b border-line/10 p-1.5">
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-dim"
                    aria-hidden
                  />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && filtered[0]) {
                        e.preventDefault();
                        choose(filtered[0].value);
                      }
                    }}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-token-sm border border-line/20 bg-surface-2 py-1.5 pl-7 pr-2 text-sm outline-none focus:border-accent/40"
                  />
                </div>
              </div>
              <div className="overflow-auto p-1">
                {filtered.length === 0 ? (
                  <p className="px-2 py-3 text-center text-xs text-fg-dim">No matches</p>
                ) : (
                  filtered.map((o) => {
                    const isSel = o.value === value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        role="option"
                        aria-selected={isSel}
                        onClick={() => choose(o.value)}
                        className={`flex w-full items-start gap-2 rounded-token-sm px-2 py-1.5 text-left text-sm transition-colors ${
                          isSel ? 'bg-accent/10 text-fg' : 'text-fg-muted hover:bg-surface-2'
                        }`}
                      >
                        <Check
                          className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isSel ? 'text-accent' : 'invisible'}`}
                          aria-hidden
                        />
                        <span className="min-w-0">
                          <span className="block leading-tight">{o.label}</span>
                          {o.description ? (
                            <span className="mt-0.5 block text-[11px] text-fg-dim">{o.description}</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
